import React from 'react';
import { Badge, Col, Row } from 'react-bootstrap';
import { FaCheckCircle } from 'react-icons/fa';
import dayjs from 'dayjs';
import type { GetIncidentActionResponse } from './hooks/incidentActionHooks';
import {
  checklistItemStatus,
  normalizeChecklistItems,
} from './actionChecklistModel';
import { SubTaskCompletionRing } from './SubTaskCompletionRing';

type ActionDetailsBodyProps = {
  action: GetIncidentActionResponse | (Record<string, any> & { name?: string });
};

const formatDate = (date: Date | string | null | undefined) => {
  if (!date) return 'Not set';
  return dayjs(date).format('MM/DD/YYYY HH:mm');
};

const getStatusBadgeClass = (status: string | null | undefined) => {
  switch (status) {
    case 'Planned':
      return 'bg-info';
    case 'Current/Ongoing':
      return 'bg-warning';
    case 'Blocked':
      return 'bg-danger';
    case 'Closed':
      return 'bg-success';
    default:
      return 'bg-secondary';
  }
};

/** Open work past its target date. A Closed action is done, however late. */
const isActionOverdue = (action: ActionDetailsBodyProps['action']) => {
  if (!action?.target_date || action.status === 'Closed') return false;
  return new Date(action.target_date) < new Date();
};

/** Shared read-only action details for ActionView page and ActionViewModal. */
const ActionDetailsBody: React.FC<ActionDetailsBodyProps> = ({ action }) => {
  const overdue = isActionOverdue(action);
  const checklist = Array.isArray(action.checklist) ? action.checklist : [];

  return (
    <div style={{ color: 'var(--text-white)' }}>
      <Row className="mb-3">
        <Col sm={3}>
          <strong>Task:</strong>
        </Col>
        <Col sm={9}>{action.name}</Col>
      </Row>

      <Row className="mb-3">
        <Col sm={3}>
          <strong>Status:</strong>
        </Col>
        <Col sm={9}>
          <Badge className={getStatusBadgeClass(action.status)}>
            {action.status}
          </Badge>
          {overdue && (
            <Badge bg="danger" className="ms-2">
              Overdue
            </Badge>
          )}
        </Col>
      </Row>

      <Row className="mb-3">
        <Col sm={3}>
          <strong>Priority:</strong>
        </Col>
        <Col sm={9}>{(action as any).priority || 'Normal'}</Col>
      </Row>

      <Row className="mb-3">
        <Col sm={3}>
          <strong>Description:</strong>
        </Col>
        <Col sm={9}>{action.description || 'No description provided'}</Col>
      </Row>

      <Row className="mb-3">
        <Col sm={3}>
          <strong>Assignee:</strong>
        </Col>
        <Col sm={9}>
          <span>{action.poc_user_name || 'Unassigned'}</span>
          {action.briefed_poc && (
            <Badge bg="success" className="ms-2">
              <FaCheckCircle className="me-1" />
              Briefed
            </Badge>
          )}
        </Col>
      </Row>

      <Row className="mb-3">
        <Col sm={3}>
          <strong>Start Date:</strong>
        </Col>
        <Col sm={9}>{formatDate(action.start_date)}</Col>
      </Row>

      <Row className="mb-3">
        <Col sm={3}>
          <strong>Target Date:</strong>
        </Col>
        <Col sm={9}>{formatDate(action.target_date)}</Col>
      </Row>

      <Row className="mb-3">
        <Col sm={3}>
          <strong>Actual Date:</strong>
        </Col>
        <Col sm={9}>{formatDate(action.actual_date)}</Col>
      </Row>

      <Row className="mb-3">
        <Col sm={3}>
          <strong>Location:</strong>
        </Col>
        <Col sm={9}>
          {(action as any).geolocation?.address ||
            (action as any).geolocation?.name ||
            ((action as any).geolocation
              ? 'Map location set'
              : 'No location set')}
        </Col>
      </Row>

      <Row className="mb-3">
        <Col sm={3}>
          <strong>Created By:</strong>
        </Col>
        <Col sm={9}>{action.user_name || 'Unknown'}</Col>
      </Row>

      <Row className="mb-3">
        <Col sm={3}>
          <strong>Last Updated:</strong>
        </Col>
        <Col sm={9}>{formatDate(action.timestamp)}</Col>
      </Row>

      {checklist.length > 0 && (
        <>
          <hr className="my-4" />
          <div className="tw:mb-3 tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            <h6 className="tw:m-0">Checklist</h6>
            <SubTaskCompletionRing checklist={checklist} />
          </div>
          <ul className="list-unstyled">
            {normalizeChecklistItems(checklist).map((item, index) => {
              const attachments = item.attachments ?? [];
              const note = (item.note ?? '').trim();
              const status = checklistItemStatus(item);

              return (
                <li key={index} className="mb-2">
                  <div className="d-flex align-items-start">
                    <Badge className={`${getStatusBadgeClass(status)} me-2`}>
                      {status}
                    </Badge>
                    <span>{item.label}</span>
                  </div>
                  {/* Shown whatever the flag says: whoever is reading the
                      action needs the note and the evidence behind a step. */}
                  {note ? (
                    <div
                      className="text-muted small ms-4 mt-1"
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {note}
                    </div>
                  ) : null}
                  {attachments.length > 0 ? (
                    <div className="text-muted small ms-4">
                      {attachments
                        .map((file) => file.label || 'Attached file')
                        .join(', ')}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
};

export default ActionDetailsBody;
